import React from 'react';
import {
  FaHeadphones,
  FaLaptop,
  FaRecordVinyl,
  FaTools
} from 'react-icons/fa';
import styled from 'styled-components';
import { vinylSalesImage } from '../assets/images';
import ContactForm from '../components/ContactForm';
import Hero from '../components/Hero';
import SectionTitle from '../components/SectionTitle';
import SEO from '../components/SEO';

// Define equipment images
const turntableImage = '/images/turntable.jpeg'; // Turntable image
const mixerImage = '/images/DJ_Mixer.jpeg'; // Mixer image
const headphonesImage = '/images/AdobeStock_561319125.jpeg'; // Headphones image

const VinylSales = () => {
  // Function to handle smooth scrolling to anchor
  const handleScrollToSection = (e) => {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute('href').substring(1);
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetTop - 120, // Increased offset to ensure headers are fully visible
        behavior: 'smooth'
      });
    }
  };

  const features = [
    'Vintage vinyl record sales',
    'Professional DJ equipment & audio gear',
    'Turntable calibration & maintenance',
    'Analog & digital DJ setup consultation',
    'Rare and collectible vinyl records',
    'High-quality audio equipment',
    'Expert advice on equipment selection',
    'Custom DJ setup design'
  ];

  return (
    <main>
      <SEO 
        title="Vinyl & Equipment Sales | TK DJ Services"
        description="Browse our curated collection of vintage vinyl records, professional DJ equipment, and audio gear. Expert advice on equipment selection and setup."
        url="/vinyl-sales"
        canonical="/vinyl-sales"
        image="/images/AdobeStock_930062517.jpeg"
      />
      <Hero 
        backgroundImage={vinylSalesImage}
        title="Vinyl & Equipment Sales"
        subtitle="Vintage vinyl records, professional DJ equipment, and audio gear for collectors and professionals."
        buttonText="Explore Collection"
        buttonLink="#collection"
      />
      
      <ServiceDetailSection>
        <div className="container">
          <SectionTitle 
            title="Curated Collection for DJs & Collectors" 
            subtitle="We offer a carefully selected range of premium audio equipment and accessories specifically chosen for professional DJs and audio enthusiasts."
            center
          />
          
          <CollectionContent>
            <CollectionImage src="/images/AdobeStock_71436710.jpeg" alt="Premium DJ and audio equipment collection" />
            <CollectionInfo>
              <h3>Premium Equipment Selection</h3>
              <p>
                At TK DJ Services, we understand that quality equipment is essential for exceptional audio experiences. 
                Our curated collection includes top-tier brands and models that meet our rigorous standards for performance, 
                reliability, and sound quality.
              </p>
              <CollectionFeatures>
                <CollectionFeatureItem>Professional-grade mixers and controllers</CollectionFeatureItem>
                <CollectionFeatureItem>High-performance speakers and monitors</CollectionFeatureItem>
                <CollectionFeatureItem>Premium headphones for critical listening</CollectionFeatureItem>
                <CollectionFeatureItem>Specialized equipment for venue installations</CollectionFeatureItem>
                <CollectionFeatureItem>Custom audio solutions for unique requirements</CollectionFeatureItem>
              </CollectionFeatures>
              <CollectionCTA href="#contact" onClick={handleScrollToSection}>Explore Our Collection</CollectionCTA>
            </CollectionInfo>
          </CollectionContent>
        </div>
      </ServiceDetailSection>
      
      <CollectionSection id="collection">
        <div className="container">
          <SectionTitle 
            title="Our Collection" 
            subtitle="Explore our curated selection of vinyl records and professional audio equipment."
            center
          />
          
          <CategoryGrid>
            <CategoryCard>
              <CategoryIcon>
                <FaRecordVinyl />
              </CategoryIcon>
              <CategoryTitle>Vintage Vinyl Records</CategoryTitle>
              <CategoryDescription>
                Our carefully curated vinyl collection spans multiple genres and decades, featuring rare pressings, classic albums, and hidden gems. From jazz and soul to electronic and hip-hop, we source quality vinyl for collectors and DJs alike. Browse our Discogs store for our current inventory of vintage and contemporary records.
              </CategoryDescription>
              <CategoryLink href="https://www.discogs.com/seller/TGonzo85/profile" target="_blank" rel="noopener noreferrer">
                Browse on Discogs
              </CategoryLink>
            </CategoryCard>
            
            <CategoryCard>
              <CategoryIcon>
                <FaHeadphones />
              </CategoryIcon>
              <CategoryTitle>Professional Audio Equipment</CategoryTitle>
              <CategoryDescription>
                We offer a selection of high-quality audio equipment including turntables, mixers, amplifiers, speakers, and headphones. Whether you're building a home studio, upgrading your DJ setup, or enhancing your listening experience, we provide professional-grade gear from trusted brands with expert guidance on selection.
              </CategoryDescription>
              <CategoryLink href="https://reverb.com/shop/trevors-boutique-286" target="_blank" rel="noopener noreferrer">
                Browse on Reverb
              </CategoryLink>
            </CategoryCard>
            
            <CategoryCard>
              <CategoryIcon>
                <FaTools />
              </CategoryIcon>
              <CategoryTitle>Turntable Calibration & Maintenance</CategoryTitle>
              <CategoryDescription>
                Keep your turntables and audio equipment in optimal condition with our professional calibration and maintenance services. We offer precision turntable setup, cartridge alignment, tonearm balancing, and regular maintenance to ensure your equipment delivers the best possible sound quality and longevity.
              </CategoryDescription>
            </CategoryCard>
            
            <CategoryCard>
              <CategoryIcon>
                <FaLaptop />
              </CategoryIcon>
              <CategoryTitle>DJ Setup Consultation</CategoryTitle>
              <CategoryDescription>
                Whether you're transitioning from analog to digital DJing or building your first setup, our consultation services provide expert guidance tailored to your needs and budget. We help you select the right equipment, design an ergonomic setup, and integrate analog and digital components for a seamless DJ experience.
              </CategoryDescription>
            </CategoryCard>
          </CategoryGrid>
        </div>
      </CollectionSection>
      
      <FeaturedSection>
        <div className="container">
          <SectionTitle 
            title="Featured Equipment" 
            subtitle="Explore some of our featured professional audio equipment and DJ gear."
            center
          />
          
          <FeaturedGrid>
            <FeaturedItem>
              <FeaturedImage style={{ backgroundImage: `url(${turntableImage})` }}>
              </FeaturedImage>
              <FeaturedTitle>Professional DJ Turntables</FeaturedTitle>
              <FeaturedDescription>
                High-torque direct drive turntables designed for professional DJ use, featuring precise pitch control, anti-skipping technology, and durable construction.
              </FeaturedDescription>
            </FeaturedItem>
            
            <FeaturedItem>
              <FeaturedImage style={{ backgroundImage: `url(${mixerImage})` }}>
              </FeaturedImage>
              <FeaturedTitle>DJ Mixers</FeaturedTitle>
              <FeaturedDescription>
                Professional-grade DJ mixers with premium audio quality, intuitive controls, and versatile connectivity options for seamless mixing and performance.
              </FeaturedDescription>
            </FeaturedItem>
            
            <FeaturedItem>
              <FeaturedImage style={{ backgroundImage: `url(${headphonesImage})` }}>
              </FeaturedImage>
              <FeaturedTitle>Professional Headphones</FeaturedTitle>
              <FeaturedDescription>
                DJ-specific headphones with excellent sound isolation, comfortable design for extended wear, and accurate sound reproduction for precise mixing.
              </FeaturedDescription>
            </FeaturedItem>
          </FeaturedGrid>
        </div>
      </FeaturedSection>
      
      <ContactSection id="contact">
        <div className="container">
          <SectionTitle 
            title="Get a Quote" 
            subtitle="Ready to elevate your venue or event with professional lighting production? Contact us today to discuss your vision."
            center
          />
          <ContactForm />
        </div>
      </ContactSection>
    </main>
  );
};

const ServiceDetailSection = styled.section`
  padding: 8rem 0 5rem;
`;

const CollectionSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.div`
  background-color: rgba(19, 19, 19, 0.5);
  border-radius: 8px;
  padding: 2rem;
  transition: transform 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const CategoryIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-secondary);
  margin-bottom: 1.5rem;
`;

const CategoryTitle = styled.h3`
  color: var(--color-tertiary);
  margin-bottom: 1rem;
`;

const CategoryDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex-grow: 1;
`;

const CategoryLink = styled.a`
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: var(--color-primary);
  color: var(--color-tertiary);
  border-radius: 4px;
  font-weight: 500;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--color-secondary);
    color: var(--color-dark);
  }
`;

const FeaturedSection = styled.section`
  padding: 5rem 0;
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeaturedItem = styled.div`
  background-color: rgba(19, 19, 19, 0.5);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const FeaturedImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const FeaturedTitle = styled.h3`
  color: var(--color-tertiary);
  margin: 1rem;
`;

const FeaturedDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
  margin: 0 1rem 1rem;
`;

const ContactSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const CollectionContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 3rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CollectionImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const CollectionInfo = styled.div`
  h3 {
    color: var(--color-tertiary);
    margin-bottom: 1.5rem;
    font-size: 1.8rem;
  }
  
  p {
    color: var(--color-tertiary);
    opacity: 0.8;
    line-height: 1.6;
    margin-bottom: 2rem;
  }
`;

const CollectionFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
`;

const CollectionFeatureItem = styled.li`
  color: var(--color-tertiary);
  opacity: 0.9;
  margin-bottom: 0.8rem;
  padding-left: 1.5rem;
  position: relative;
  
  &:before {
    content: '•';
    color: var(--color-secondary);
    position: absolute;
    left: 0;
  }
`;

const CollectionCTA = styled.a`
  display: inline-block;
  background-color: var(--color-primary);
  color: var(--color-tertiary);
  padding: 0.8rem 1.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--color-secondary);
    transform: translateY(-3px);
  }
`;

export default VinylSales; 